import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/carousel";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const pausedRef = useRef(false);
  const total = heroSlides.length;

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!pausedRef.current) {
        setIndex((prev) => (prev + 1) % total);
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [total]);

  const goTo = (next: number) => setIndex((next + total) % total);

  return (
    <section
      className="relative w-full overflow-hidden bg-foreground-950"
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
    >
      <div className="relative h-[440px] w-full md:h-[560px]">
        {heroSlides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={`${slide.title} 焦点推荐`}
              title={`${slide.title} - 91看片高清影视`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30"></div>

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full max-w-site flex-col justify-center px-4 md:px-6">
            <div key={index} className="max-w-2xl animate-fade-up">
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
                <i className="ri-fire-fill"></i>
                焦点推荐
              </span>
              <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                {heroSlides[index].title}
              </h1>
              <p className="mt-3 max-w-xl text-sm text-background-200 md:text-base">
                {heroSlides[index].tagline}
              </p>
              <p className="mt-3 text-xs text-background-300 md:text-sm">
                {heroSlides[index].meta}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${heroSlides[index].id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </Link>
                <Link
                  to={`/detail/${heroSlides[index].id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
                >
                  查看详情
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Arrows */}
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/30 md:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/30 md:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        {/* Dots */}
        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {heroSlides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? "w-7 bg-accent-500" : "w-1.5 bg-background-50/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}